import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Icon, InlineMessage } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../../../utils/localization';
import { CAMP_TYPE_RO } from '../../CertsListTile/constant';
import {
  LIST_SP_TAB_HISTORY,
  LIST_SP_TAB_PROGRESS,
  LIST_SP_TAB_SUMMARY,
} from '../constant';

import CertsListSidePanelBodyError from './CertsListSidePanelBodyError';
import CertsListSidePanelBodyHistory from './CertsListSidePanelBodyHistory';
import CertsListSidePanelBodyProgress from './CertsListSidePanelBodyProgress';
import CertsListSidePanelBodySummary from './CertsListSidePanelBodySummary';
import { invertedMessages } from './messages';

function CertsListSidePanelBodyContent({
  tab,
  sidePanelData,
  retriggerApisForError,
  handleCloseSidePanel,
  activeTab,
  setActiveTab,
}) {
  const {
    global: { globalDateFormat },
    core: { isAdminLogin },
  } = useSelector((state) => state?.certifications?.list);
  const l = useInvertedLocalize(invertedMessages);

  /**
   * Determines if the certification was launched in classic experience.
   * When true, displays an inline warning message in the Summary tab
   * indicating the certification uses the classic/legacy UX.
   * This message is shown regardless of API success or failure.
   *
   * @type {boolean}
   */
  const isClassicCert = Boolean(
    Number(sidePanelData?.sidePanelMetaData?.newCertUX) === 0
  );
  const isClassicNonRO =
    isClassicCert &&
    sidePanelData?.sidePanelMetaData?.campaignType !== CAMP_TYPE_RO;

  const isSummaryTabErrored =
    sidePanelData.networkFailedTabs.includes(LIST_SP_TAB_SUMMARY);
  const isHistoryTabErrored =
    sidePanelData.networkFailedTabs.includes(LIST_SP_TAB_HISTORY);
  const isProgressTabErrored =
    sidePanelData.networkFailedTabs.includes(LIST_SP_TAB_PROGRESS);

  const renderTabContent = () => {
    const isError = Boolean(
      (isSummaryTabErrored && tab === LIST_SP_TAB_SUMMARY) ||
        (isHistoryTabErrored && tab === LIST_SP_TAB_HISTORY) ||
        (isProgressTabErrored && tab === LIST_SP_TAB_PROGRESS)
    );

    if (isError) {
      return (
        <CertsListSidePanelBodyError
          tab={tab}
          retriggerApisForError={retriggerApisForError}
          handleCloseSidePanel={handleCloseSidePanel}
        />
      );
    }

    if (tab === LIST_SP_TAB_SUMMARY) {
      return (
        <CertsListSidePanelBodySummary
          setActiveTab={setActiveTab}
          summaryData={sidePanelData[LIST_SP_TAB_SUMMARY]}
          historyData={sidePanelData[LIST_SP_TAB_HISTORY]}
          isAdminLogin={isAdminLogin}
          globalDateFormat={globalDateFormat}
          isPrimaryCertifierActive={
            sidePanelData?.sidePanelMetaData?.isPrimaryCertifierActive
          }
          isAutoRevoke={sidePanelData?.sidePanelMetaData?.autoRevoke}
        />
      );
    }

    if (tab === LIST_SP_TAB_HISTORY) {
      return (
        <CertsListSidePanelBodyHistory
          activeTab={activeTab}
          historyData={sidePanelData[LIST_SP_TAB_HISTORY]}
          handleCloseSidePanel={handleCloseSidePanel}
        />
      );
    }

    if (tab === LIST_SP_TAB_PROGRESS) {
      return (
        <CertsListSidePanelBodyProgress
          progressData={sidePanelData[LIST_SP_TAB_PROGRESS]}
          campaignType={sidePanelData?.sidePanelMetaData?.campaignType}
        />
      );
    }

    return null;
  };

  return (
    <Box tag='div' className='CertsListSidePanelBody-tabContentWrapper'>
      {isClassicNonRO && tab === LIST_SP_TAB_SUMMARY && (
        <InlineMessage
          text={l('Viewing unavailable — Classic Experience Campaign')}
          content={l(
            'This certification was launched in the classic experience. Detailed view cannot be displayed, but your campaign data is preserved and available to download.'
          )}
          colorTheme='warning'
          size='medium'
          leftIcon={<Icon kind='AlertWarningFilled' />}
          className='CertsListSidePanelBodyContent-classicMessage'
          classNames={{
            rootClassName: 'CertsListSidePanelBodyContent-classicMessageRoot',
          }}
        />
      )}
      {renderTabContent()}
    </Box>
  );
}

CertsListSidePanelBodyContent.propTypes = {
  tab: PropTypes.string.isRequired,
  sidePanelData: PropTypes.shape({
    networkFailedTabs: PropTypes.arrayOf(PropTypes.string),
    sidePanelMetaData: PropTypes.shape({
      autoRevoke: PropTypes.bool,
      progress: PropTypes.string,
      isPrimaryCertifierActive: PropTypes.bool,
      newCertUX: PropTypes.string,
      campaignType: PropTypes.number,
    }),
  }).isRequired,
  retriggerApisForError: PropTypes.func,
  handleCloseSidePanel: PropTypes.func,
  activeTab: PropTypes.string,
  setActiveTab: PropTypes.func,
};

CertsListSidePanelBodyContent.defaultProps = {
  retriggerApisForError: () => {},
  handleCloseSidePanel: () => {},
  activeTab: '',
  setActiveTab: () => {},
};

export default CertsListSidePanelBodyContent;
